import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  makeStyles,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  IconButton,
} from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import trLocale from "date-fns/locale/tr";
import axios from "src/utils/baseAxios";
import Page from "src/components/Page";
import { getUser } from "src/utils/Common";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { connect } from "react-redux";
import { getTasksRequest } from "../../store/actions/taskActions";
import * as XLSX from "xlsx";
import GetAppIcon from "@material-ui/icons/GetApp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  titleBox: {
    background: "linear-gradient(90deg, #1b84ff 0%, #63b3ff 100%)",
    color: "#fff",
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: "inset 0 0 10px rgba(255,255,255,0.15)",
  },
  titleText: {
    color: "#28297cff",
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: 0.3,
  },
  subtitle: {
    color: "rgba(255,255,255,0.9)",
    fontSize: 15,
    fontWeight: 500,
    marginTop: theme.spacing(0.5),
  },
  card: { borderRadius: 16, boxShadow: "0 3px 10px rgba(0,0,0,0.1)" },
  tableHead: { background: "#f5f7fa" },
  th: { fontWeight: 600, color: "#333" },
  filterBox: {
    marginBottom: theme.spacing(3),
    background: "#f9fbff",
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  datePicker: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: "#fff",
    },
  },
  button: {
    height: 48,
    borderRadius: 12,
    background: "linear-gradient(90deg, #1b84ff 0%, #63b3ff 100%)",
    color: "#fff",
    fontWeight: 600,
    "&:hover": {
      background: "linear-gradient(90deg, #1673e0 0%, #4fa3ff 100%)",
    },
  },
  statusChip: {
    display: "inline-block",
    padding: "6px 14px",
    borderRadius: 16,
    fontSize: 13,
    fontWeight: 600,
    color: "#fff",
    textTransform: "capitalize",
    minWidth: 80,
    textAlign: "center",
    transition: "all 0.2s ease-in-out",
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  },
  status_new: {
    background: "linear-gradient(90deg, #1b84ff 0%, #63b3ff 100%)",
    boxShadow: "0 2px 8px rgba(27,132,255,0.4)",
  },
  status_other: {
    background: "linear-gradient(90deg, #ff9800 0%, #ffb74d 100%)",
    boxShadow: "0 2px 8px rgba(255,152,0,0.35)",
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover td": {
      backgroundColor: "rgba(255, 99, 27, 0.08) !important",
    },
  },
  cancellationNote: {
    flex: 1,
    minWidth: 0,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cancellationNoteContent: {
    display: "flex",
    alignItems: "center",
  },
  cancellationNoteExpanded: {
    maxWidth: "none",
    overflow: "visible",
    textOverflow: "clip",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  cancellationNoteToggle: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.primary.main,
  },
}));

function TradePartnerWorkOrders({ getTasksRequest }) {
  const classes = useStyles();
  const { t } = useTranslation("overview");
  const history = useHistory();
  const SessionUser = getUser();

  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(() => {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    return nextWeek;
  });
  const [workOrderId, setWorkOrderId] = useState("");
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedCancellationNotes, setExpandedCancellationNotes] = useState({});

  const localeMap = { tr: trLocale };
  const locale = localStorage.getItem("i18nextLng");

  const fetchData = async () => {
    try {
      setLoading(true);
      const employeeId = SessionUser?.TechnicianId || null;

      const params = {
        employeeId,
        startDate: startDate ? startDate.toISOString().split("T")[0] : null,
        endDate: endDate ? endDate.toISOString().split("T")[0] : null,
        serviceShopId: SessionUser?.ServiceShopId || null,
      };

      if (workOrderId) params.workOrderId = workOrderId;

      const response = await axios.get("/tradepartner/tradepartner-workorders", {
        params,
      });

      if (response.data.success !== false) {
        setData(response.data.data || response.data);
        setPage(0);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusClass = (status) => {
    if (!status) return classes.status_other;
    const s = status.toLowerCase();
    if (s.includes("new")) return classes.status_new;
    return classes.status_other;
  };

  const getDisplaySerial = ({ Barcode, SKU, SerialNr }) =>
    Barcode && SKU && Barcode.startsWith(SKU)
      ? Barcode.substring(SKU.length)
      : SerialNr;

  const toggleCancellationNote = (event, workOrderId) => {
    event.stopPropagation();
    setExpandedCancellationNotes((current) => ({
      ...current,
      [workOrderId]: !current[workOrderId],
    }));
  };

  const handleExportExcel = () => {
    if (!data || data.length === 0) return;

    const formattedData = data.map((item) => ({
      WorkCaseId: item.WorkCaseId,
      WorkOrderId: item.WorkOrderId,
      Status: item.StatusFieldText,
      DealerNr: item.DealerNr,
      PreferredSolution: item.PreferredSolution,
      Product: item.Product || "",
      WarrantyType: item.WarrantyType || "",
      SerialNr: getDisplaySerial(item) || "",
      Brand: item.Brand || "",
      OpenDate: item.OpenDate
        ? new Date(item.OpenDate).toLocaleDateString("tr-TR")
        : "",
      WCDate: item.WCDate
        ? new Date(item.WCDate).toLocaleDateString("tr-TR")
        : "",
      AppointmentDate: item.AppointmentDate
        ? new Date(item.AppointmentDate).toLocaleDateString("tr-TR")
        : "",
      RepairDate: item.VerificationDate
        ? new Date(item.VerificationDate).toLocaleDateString("tr-TR")
        : "",
      FinalNote: item.FinalNote || "",
      CancellationReason: item.CancellationReason || "",
      CancellationNote: item.CancellationNote || "",
      ExchangeType: item.ExchangeType || "",
      Defect: item.Defect || "",
    }));

    // 📊 Sheet
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    const columnWidths = [
      { wch: 15 }, // WorkCaseId
      { wch: 15 }, // WorkOrderId
      { wch: 20 }, // Status
      { wch: 15 }, // DealerNr
      { wch: 25 }, // PreferredSolution
      { wch: 20 }, // Product
      { wch: 18 }, // WarrantyType
      { wch: 18 }, // SerialNr
      { wch: 15 }, // Brand
      { wch: 15 }, // OpenDate
      { wch: 15 }, // WCDate
      { wch: 18 }, // AppointmentDate
      { wch: 18 }, // RepairDate
      { wch: 25 }, // FinalNote
      { wch: 25 }, // CancellationReason
      { wch: 35 }, // CancellationNote
      { wch: 18 }, // ExchangeType
      { wch: 25 }, // Defect
    ];
    worksheet["!cols"] = columnWidths;

    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "WorkOrders");
    XLSX.writeFile(workbook, "TradePartnerWorkOrders.xlsx");
  };

  const handleRowClick = async (item) => {
    try {
      const Search = String(item.WorkOrderId);
      const SessionUser = getUser();

      const res = await axios.get(
        `/technician/GetConsumerByWorkOrderId/${Search}/${SessionUser.ServiceShopId}/${SessionUser.Role}`
      );

      const response = res.data[0];
      if (response) {
        sessionStorage.removeItem("SearhResult");
        sessionStorage.setItem("SearhResult", JSON.stringify(response));

        sessionStorage.removeItem("ConsumerId");
        sessionStorage.setItem("ConsumerId", response.ConsumerId);

        getTasksRequest(Search);
        history.push(`/WorkOrder/${Search}/summary`);
      }
    } catch (err) {
      console.error("Error fetching consumer:", err);
    }
  };

  const pagedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Page title={t("tradePartnerWorkOrders", "Trade Partner Work Orders")} className={classes.root}>
      <Box className={classes.titleBox}>
        <Typography className={classes.titleText}>
          📋 {t("tradePartnerWorkOrders", "Trade Partner Work Orders")}
        </Typography>
        <Typography className={classes.subtitle}>
          {t("trackAndManageWorkOrders", "Track and manage all work orders assigned to you.")}
        </Typography>
      </Box>

      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.filterBox}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[locale]}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <KeyboardDatePicker
                    className={classes.datePicker}
                    variant="inline"
                    inputVariant="outlined"
                    fullWidth
                    label={t("startDate", "Start Date")}
                    format="dd/MM/yyyy"
                    value={startDate}
                    onChange={(date) => setStartDate(date)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KeyboardDatePicker
                    className={classes.datePicker}
                    variant="inline"
                    inputVariant="outlined"
                    fullWidth
                    label={t("endDate", "End Date")}
                    format="dd/MM/yyyy"
                    value={endDate}
                    onChange={(date) => setEndDate(date)}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("workOrderId", "WorkOrder Id")}
                    value={workOrderId}
                    onChange={(e) => setWorkOrderId(e.target.value)}
                    InputProps={{
                      style: { borderRadius: 12, background: "#fff" },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={3} md={2}>
                  <Button
                    fullWidth
                    className={classes.button}
                    onClick={fetchData}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t("search", "Search")
                    )}
                  </Button>
                </Grid>

                <Grid item xs={12} sm={3} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    style={{
                      height: 48,
                      borderRadius: 12,
                      borderColor: "#1b84ff",
                      color: "#1b84ff",
                      fontWeight: 600,
                    }}
                    onClick={handleExportExcel}
                    startIcon={<GetAppIcon />}
                    disabled={!data || data.length === 0}
                  >
                    {t("exportExcel", "Export Excel")}
                  </Button>
                </Grid>
              </Grid>
            </MuiPickersUtilsProvider>
          </Box>

          {loading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                    <TableCell className={classes.th}>{t("workCaseId", "WorkCaseId")}</TableCell>
                    <TableCell className={classes.th}>{t("workOrderId", "WorkOrderId")}</TableCell>
                    <TableCell className={classes.th} style={{ minWidth: 200 }}>{t("status", "Status")}</TableCell>
                    <TableCell className={classes.th}>{t("dealerNr", "Dealer Nr")}</TableCell>
                    <TableCell className={classes.th}>{t("preferredSolution", "Preferred Solution")}</TableCell>
                    <TableCell className={classes.th}>{t("product", "Product")}</TableCell>
                    <TableCell className={classes.th}>{t("warrantyType", "WarrantyType")}</TableCell>
                    <TableCell className={classes.th}>{t("serialNr", "SerialNr")}</TableCell>
                    <TableCell className={classes.th}>{t("brand", "Brand")}</TableCell>
                    <TableCell className={classes.th}>{t("openDate", "WO Open Date")}</TableCell>
                    <TableCell className={classes.th}>{t("wcDate", "WC Date")}</TableCell>
                    <TableCell className={classes.th}>{t("appointmentDate", "Appointment Date")}</TableCell>
                    <TableCell className={classes.th}>{t("repairDate", "Repair Date")}</TableCell>
                    <TableCell className={classes.th}>{t("finalNote", "Final Note")}</TableCell>
                    <TableCell className={classes.th}>{t("cancellationReason", "Cancellation Reason")}</TableCell>
                    <TableCell className={classes.th} style={{ minWidth: 220 }}>{t("cancellationNote", "Cancellation Note")}</TableCell>
                    <TableCell className={classes.th}>{t("exchangeType", "Exchange Type")}</TableCell>
                    <TableCell className={classes.th} style={{ minWidth: 100 }}>{t("defect", "Defect")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedData.length > 0 ? (
                      pagedData.map((item, index) => {
                        const isCancellationNoteExpanded = expandedCancellationNotes[item.WorkOrderId];

                        return (
                          <TableRow
                            key={item.WorkOrderId || index}
                            hover
                            className={classes.clickableRow}
                            onClick={() => handleRowClick(item)}
                          >
                            <TableCell>{item.WorkCaseId}</TableCell>
                            <TableCell>{item.WorkOrderId}</TableCell>
                            <TableCell style={{ minWidth: 200 }}>
                              <span className={`${classes.statusChip} ${getStatusClass(item.StatusFieldText)}`}>
                                {item.StatusFieldText || "-"}
                              </span>
                            </TableCell>
                            <TableCell>{item.DealerNr}</TableCell>
                            <TableCell>{item.PreferredSolution}</TableCell>
                            <TableCell>{item.Product}</TableCell>
                            <TableCell>{item.WarrantyType}</TableCell>
                            <TableCell>{getDisplaySerial(item)}</TableCell>
                            <TableCell>{item.Brand}</TableCell>
                            <TableCell>{item.OpenDate ? new Date(item.OpenDate).toLocaleDateString() : "-"}</TableCell>
                            <TableCell>{item.WCDate ? new Date(item.WCDate).toLocaleDateString() : "-"}</TableCell>
                            <TableCell>{item.AppointmentDate ? new Date(item.AppointmentDate).toLocaleDateString() : "-"}</TableCell>
                            <TableCell>{item.VerificationDate ? new Date(item.VerificationDate).toLocaleDateString() : "-"}</TableCell>
                            <TableCell>{item.FinalNote || "-"}</TableCell>
                            <TableCell>{item.CancellationReason || "-"}</TableCell>
                            <TableCell style={{ minWidth: 220 }}>
                              <Box className={classes.cancellationNoteContent}>
                                <span className={`${classes.cancellationNote} ${isCancellationNoteExpanded ? classes.cancellationNoteExpanded : ""}`}>
                                  {item.CancellationNote || "-"}
                                </span>
                                {item.CancellationNote && (
                                  <IconButton
                                    aria-label={isCancellationNoteExpanded
                                      ? t("hideCancellationNote", "Hide cancellation note")
                                      : t("showCancellationNote", "Show cancellation note")}
                                    className={classes.cancellationNoteToggle}
                                    size="small"
                                    onClick={(event) => toggleCancellationNote(event, item.WorkOrderId)}
                                  >
                                    {isCancellationNoteExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{item.ExchangeType || "-"}</TableCell>
                            <TableCell style={{ minWidth: 100 }}>{item.Defect || "-"}</TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={18} align="center">
                          {t("noRecordsFound", "No records found.")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={data.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onChangePage={(event, newPage) => setPage(newPage)}  
                onChangeRowsPerPage={(event) => {                     
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Page>
  );
}

export default connect(null, { getTasksRequest })(TradePartnerWorkOrders);
